import { Alert } from '@televet/kibble-ui/Alert';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Tag } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { Flex } from '@televet/kibble-ui/chakra';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { CopyableId } from '@/components/CopyableId';
import { Field } from '@/components/Field';
import { PageContainer } from '@/components/PageContainer';
import { SummaryCard } from '@/components/SummaryCard';
import { trpc } from '@/lib/trpc';
import { workflowStatusVariantMap } from '@/features/workflows/badgePalettes';
import { StepCard } from '@/features/workflows/components/StepCard';
import { EventListenersCard } from '@/features/workflows/components/EventListenersCard';
import { InformCampaignSection } from '@/features/workflows/components/InformCampaignSection';
import { WorkflowKeySection } from '@/features/workflows/components/WorkflowKeySection';
import { DownloadWorkflowDocumentButton } from '@/features/workflows/components/DownloadWorkflowDocumentButton';
import { ConfigurationsCard } from '@/features/workflows/components/ConfigurationsCard';
import { CommunicationOptOutsCard } from '@/features/workflows/components/CommunicationOptOutsCard';
import { CampaignOptOutsCard } from '@/features/workflows/components/CampaignOptOutsCard';

const RouteComponent = () => {
  const { workflowId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: workflow } = useQuery(
    trpc.workflow.findOneWorkflow.queryOptions({ id: workflowId })
  );

  if (location.pathname.includes('/tasks/')) {
    return <Outlet />;
  }

  if (!workflow) {
    return (
      <PageContainer>
        <Flex justify="center" align="center" py={16} width="100%">
          <Spinner size="lg" />
        </Flex>
      </PageContainer>
    );
  }

  // Workflow-level warnings carry no key — they are shown in the banner, not
  // pinned to one task definition.
  const warnedTaskKeys = new Set(
    workflow.completionWarnings
      .map((warning) => warning.taskDefinitionKey)
      .filter((key): key is string => !!key)
  );
  const taskCount = workflow.steps.reduce((total, step) => total + step.taskDefinitions.length, 0);

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={6}>
        <Breadcrumbs items={[{ label: 'Workflows', to: '/workflows' }, { label: workflow.name }]} />

        <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap">
          <Flex direction="column" gap={1}>
            <Flex align="center" gap={3} wrap="wrap">
              <Text fontSize="2xl" fontWeight="bold">
                {workflow.name}
              </Text>
              <Tag
                label={workflow.status}
                variant={workflowStatusVariantMap[workflow.status] ?? 'default'}
                size="lg"
              />
              <Tag label={`v${workflow.version}`} variant="default" size="lg" />
            </Flex>
            {workflow.description && (
              <Text fontSize="sm" variant="subtle">
                {workflow.description}
              </Text>
            )}
          </Flex>
          <DownloadWorkflowDocumentButton workflowId={workflowId} workflowKey={workflow.key} />
        </Flex>

        <Flex gap={4} wrap="wrap" align="stretch">
          <SummaryCard title="DEFINITION">
            <Field label="Run schema">
              {/* A dash for absent, the same placeholder the configurations table uses. */}
              <Text
                fontSize="sm"
                fontFamily={workflow.runSchema ? 'mono' : undefined}
                variant={workflow.runSchema ? undefined : 'subtle'}
              >
                {workflow.runSchema ?? '—'}
              </Text>
            </Field>
            <Field label="Steps" minW="70px">
              <Text fontSize="sm">{workflow.steps.length}</Text>
            </Field>
            <Field label="Tasks" minW="70px">
              <Text fontSize="sm">{taskCount}</Text>
            </Field>
            <Field label="Configurations" minW="100px">
              <Text fontSize="sm">{workflow._count.configurations}</Text>
            </Field>
            <Field label="Workflow ID" minW="100%">
              <CopyableId id={workflowId} label="workflow" />
            </Field>
          </SummaryCard>

          <SummaryCard title="SELF-ENROLLMENT">
            <WorkflowKeySection workflowId={workflowId} workflowKey={workflow.key} />
          </SummaryCard>

          <SummaryCard title="INFORM CAMPAIGN">
            <InformCampaignSection
              workflowId={workflowId}
              informCampaignId={workflow.informCampaignId}
              informCampaign={workflow.informCampaign}
              source={workflow.informCampaignSource}
              ambiguous={workflow.informCampaignAmbiguous}
            />
          </SummaryCard>
        </Flex>

        {/* Completion misconfigurations — warnings only; nothing here blocks a save. */}
        {workflow.completionWarnings.length > 0 && (
          <Flex direction="column" gap={2} data-testid="workflow-completion-warnings">
            {workflow.completionWarnings.map((warning) => (
              <Alert
                key={`${warning.code}-${warning.taskDefinitionKey ?? 'workflow'}`}
                status="warning"
                hideCloseButton
                title="This workflow cannot complete as configured"
                description={warning.message}
              />
            ))}
          </Flex>
        )}

        <Flex direction="column" gap={4}>
          <Text fontSize="lg" fontWeight="bold">
            Steps
          </Text>
          {workflow.steps.map((step, stepIndex) => (
            <StepCard
              key={step.id}
              step={step}
              stepIndex={stepIndex}
              isStartingStep={step.id === workflow.startingStepId}
              workflowId={workflowId}
              warnedTaskKeys={warnedTaskKeys}
              emailBodies={workflow.emailBodies}
              onEditTask={(taskDefId) =>
                navigate({
                  to: '/workflows/$workflowId/tasks/$taskDefId',
                  params: { workflowId, taskDefId },
                })
              }
            />
          ))}
        </Flex>

        {/* Each section titled outside its card, the way Steps is, so the page reads as a
            list of sections rather than a stack of boxes that each name themselves. */}
        <Flex direction="column" gap={4}>
          <Text fontSize="lg" fontWeight="bold">
            Event listeners
          </Text>
          <EventListenersCard workflowId={workflowId} eventListeners={workflow.eventListeners} />
        </Flex>

        <Flex direction="column" gap={4}>
          <Text fontSize="lg" fontWeight="bold">
            Configurations
          </Text>
          <ConfigurationsCard workflowId={workflowId} />
        </Flex>

        <Flex direction="column" gap={4}>
          <Text fontSize="lg" fontWeight="bold">
            Communication Opt-Outs
          </Text>

          {/* Two sources under one heading: astro's own rows, then Inform's. Inform's
              suppress this workflow's sends but nothing here can act on them. */}
          <Flex direction="column" gap={2}>
            <Text fontSize="sm" fontWeight="medium">
              Workflow
            </Text>
            <CommunicationOptOutsCard workflowId={workflowId} />
          </Flex>

          {/* Gated on `informCampaignId`, not on the resolved campaign. `bundleRenderer` reads
              only that column into the safeguard context, so a workflow without it gets no
              campaign-level suppression at all — showing opt-outs there would claim one.
              The procedure finds which namespace holds the id. */}
          {workflow.informCampaignId && (
            <Flex direction="column" gap={2}>
              <Text fontSize="sm" fontWeight="medium">
                Inform Campaign
              </Text>
              <CampaignOptOutsCard informCampaignId={workflow.informCampaignId} />
            </Flex>
          )}
        </Flex>
      </Flex>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_authenticated/workflows/$workflowId')({
  component: RouteComponent,
});
